import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, CheckSquare, CheckCircle, ClipboardList, ShoppingCart, FileText, PhoneCall, PhoneIncoming, PhoneOutgoing, RefreshCw, Loader2, Printer, Send, FileCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useV3Alerts } from './V3AlertsContext';
import { useV3Prefs } from './V3PrefsContext';

// dd/mm/yy, as everywhere else in the dashboard
const fmtDate = (d) => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
  } catch (_) { return ''; }
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } catch (_) { return ''; }
};

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const money = (value) => {
  if (!value) return '—';
  return value >= 1000 ? `£${(value / 1000).toFixed(1)}k` : `£${value.toFixed(0)}`;
};

const Section = ({ title, count, tone, action, children, testId }) => (
  <section className="v3-section" data-testid={testId}>
    <div className="v3-section-head">
      <span className="v3-section-title">{title}</span>
      {count !== undefined && <span className={`v3-count ${tone ? `v3-count--${tone}` : ''}`}>{count}</span>}
      {action}
    </div>
    {children}
  </section>
);

const Row = ({ icon: Icon, title, meta, badge, tone, onClick, testId, children }) => (
  <div className={`v3-item ${tone === 'alert' ? 'v3-item--alert' : ''}`} data-testid={testId}>
    <button type="button" className="v3-item-button" onClick={onClick} title={meta ? `${title} · ${meta}` : title}>
      <span className={`v3-item-ico ${tone === 'alert' ? 'v3-item-ico--alert' : ''}`}><Icon className="v3-i v3-i--sm" /></span>
      <span className="v3-item-text">
        <span className="v3-item-title">{title}</span>
        {meta && <span className="v3-item-meta">{meta}</span>}
      </span>
      {badge && <span className={`v3-badge ${tone === 'alert' ? 'v3-badge--alert' : ''}`}>{badge}</span>}
    </button>
    {children}
  </div>
);

// What the Home page notification cards show, one panel per kind, each opened from its own coloured icon on the right bar. Each row opens
// the same window it opens on the Home page (task details, log a call, 8x8 calls, quotes and orders) or goes to the same page.
// Which of them appear for a person is decided by Settings > Dashboard > Notification Bar, through useV3Alerts().show.
const useHelpers = () => {
  const navigate = useNavigate();
  const { closeOverlay } = useV3Prefs();
  const go = (path) => { navigate(path); closeOverlay(); };
  const accountOf = (item) => item.account_name || (item.account?.module?.api_name === 'Accounts' ? item.account?.name : null) || item.account?.name || item.contact?.name || '—';
  return { go, accountOf };
};

// Tasks (blue): overdue first, then today's, then what was completed today.
export const TasksTab = () => {
  const alerts = useV3Alerts();
  const { accountOf } = useHelpers();
  return (
    <div className="v3-tasks" data-testid="v3-tab-tasks">
      <Section title="Tasks" count={alerts.overdueTasks.length + alerts.todayTasks.length} tone={alerts.overdueTasks.length > 0 ? 'alert' : undefined} testId="v3-section-tasks">
          {alerts.tasksLoading && <p className="v3-empty"><Loader2 className="v3-i v3-i--sm v3-spin" /> Loading tasks…</p>}
          {!alerts.tasksLoading && alerts.overdueTasks.length === 0 && alerts.todayTasks.length === 0 && (
            <p className="v3-empty">No tasks due today</p>
          )}
          {alerts.overdueTasks.map((task) => (
            <Row key={task.id} icon={CheckSquare} tone="alert" title={accountOf(task)} meta={`${task.subject || ''} · due ${fmtDate(task.due_date)}`} onClick={() => alerts.openTask(task, false)} testId={`v3-task-${task.id}`} />
          ))}
          {alerts.todayTasks.map((task) => (
            <Row key={task.id} icon={CheckSquare} title={accountOf(task)} meta={task.subject} badge="Today" onClick={() => alerts.openTask(task, false)} testId={`v3-task-${task.id}`} />
          ))}
        </Section>

      {(alerts.completedTasks.length > 0 || alerts.completedCalls.length > 0) && (
        <Section title="Completed today" count={alerts.completedTasks.length + alerts.completedCalls.length} tone="done" testId="v3-section-completed">
          {alerts.completedTasks.map((task) => (
            <Row key={task.id} icon={CheckCircle} title={accountOf(task).replace(/\s*-\s*\d{4,}$/, '').trim() || '—'} meta={task.subject} badge="Task" onClick={() => alerts.openTask(task, true)} testId={`v3-done-task-${task.id}`} />
          ))}
          {alerts.completedCalls.map((call) => (
            <Row key={call.id} icon={CheckCircle} title={accountOf(call)} meta={`${call.subject || ''}${call.completed_time ? ` · ${fmtDate(call.completed_time)}` : ''}`} badge="Call" onClick={() => {}} testId={`v3-done-call-${call.id}`} />
          ))}
        </Section>
      )}
    </div>
  );
};

// Scheduled calls (purple): today's scheduled CRM calls, overdue first.
export const ScheduledCallsTab = () => {
  const alerts = useV3Alerts();
  const { accountOf } = useHelpers();
  return (
    <div className="v3-tasks" data-testid="v3-tab-calls">
      <Section
          title="Scheduled calls"
          count={alerts.calls.length}
          tone={alerts.overdueCalls.length > 0 ? 'alert' : undefined}
          testId="v3-section-calls"
          action={(
            <button type="button" className="v3-section-action" onClick={alerts.manualRefreshCalls} title="Refresh calls" aria-label="Refresh calls" data-testid="v3-calls-refresh">
              <RefreshCw className={`v3-i v3-i--sm ${alerts.callsRefreshing ? 'v3-spin' : ''}`} />
            </button>
          )}
        >
          {!alerts.callsLoading && alerts.calls.length === 0 && <p className="v3-empty">No calls scheduled today</p>}
          {alerts.overdueCalls.map((call) => (
            <Row key={call.id} icon={Phone} tone="alert" title={accountOf(call)} meta={`${call.account?.phone || call.phone || 'No number on file'}${call.description ? ` · ${call.description}` : ''}`} badge="Overdue" onClick={() => alerts.openCall(call)} testId={`v3-call-${call.id}`} />
          ))}
          {alerts.todayCalls.map((call) => (
            <Row key={call.id} icon={Phone} title={accountOf(call)} meta={`${call.account?.phone || call.phone || 'No number on file'}${call.tag ? ` · ${call.tag}` : (call.description ? ` · ${call.description}` : '')}`} onClick={() => alerts.openCall(call)} testId={`v3-call-${call.id}`} />
          ))}
        </Section>
    </div>
  );
};

// PI counts (teal).
export const PiTab = () => {
  const alerts = useV3Alerts();
  const { go } = useHelpers();
  const [openPiId, setOpenPiId] = useState(null);
  const piRows = alerts.piCounts;
  return (
    <div className="v3-tasks" data-testid="v3-tab-pi">
      <Section title="PI counts" count={piRows.length} testId="v3-section-pi">
          {!alerts.piLoading && piRows.length === 0 && <p className="v3-empty">No pending PI counts</p>}
          {piRows.map((count) => {
            const isAdjustment = count.action_type === 'Adjustment';
            const label = isAdjustment ? 'Adjustment' : (count.action_type === 'Office Check' ? 'Office Check' : 'Ready to count');
            const total = count.total || 0;
            const counted = count.counted || 0;
            const meta = `${label} · ${fmtDate(count.date)}${total > 0 ? ` · ${total} item${total === 1 ? '' : 's'}${!isAdjustment && counted ? ` · ${counted}/${total}` : ''}` : ''}`;
            const isOpen = openPiId === count.id;
            return (
              <Row
                key={count.id}
                icon={ClipboardList}
                title={count.supplier_name}
                meta={meta}
                badge={count.is_recount ? `R${count.recount_number || 1}` : undefined}
                onClick={() => (isAdjustment ? go(`/pi-counts/adjustment/${count.id}`) : setOpenPiId(isOpen ? null : count.id))}
                testId={`v3-pi-${count.id}`}
              >
                {isOpen && !isAdjustment && (
                  <div className="v3-item-actions">
                    <button type="button" className="v3-mini" onClick={() => go(`/pi-counts/${count.id}`)}><FileCheck className="v3-i v3-i--sm" />Open count</button>
                    <button type="button" className="v3-mini" onClick={() => go(`/pi-counts?action=send-to-wh&countId=${encodeURIComponent(count.id)}`)}><Send className="v3-i v3-i--sm" />Send to W/H</button>
                    <button type="button" className="v3-mini" onClick={() => go(`/pi-counts/${count.id}?action=print`)}><Printer className="v3-i v3-i--sm" />Print</button>
                  </div>
                )}
              </Row>
            );
          })}
        </Section>
    </div>
  );
};

// Stock requests (amber).
export const StockTab = () => {
  const alerts = useV3Alerts();
  const { go } = useHelpers();
  const stockUsers = alerts.stockSummary?.users || [];
  return (
    <div className="v3-tasks" data-testid="v3-tab-stock">
      <Section title="Stock requests" count={alerts.stockSummary?.total_active || 0} testId="v3-section-stock">
          {stockUsers.length === 0 && <p className="v3-empty">No pending requests</p>}
          {stockUsers.map((entry, index) => (
            <Row
              key={`${entry.user_email || entry.user_name}-${index}`}
              icon={ShoppingCart}
              title={entry.user_name}
              meta={`${entry.count} item${entry.count !== 1 ? 's' : ''}${entry.suppliers && entry.suppliers.length > 0 ? ` · ${entry.suppliers.join(', ')}` : ''}`}
              onClick={() => go(`/stock-management?requestUser=${encodeURIComponent(entry.user_email)}`)}
              testId={`v3-stock-${index}`}
            />
          ))}
        </Section>
    </div>
  );
};

// Quotes and orders today (indigo).
export const OrdersTab = () => {
  const alerts = useV3Alerts();
  return (
    <div className="v3-tasks" data-testid="v3-tab-orders">
      <Section title="Quotes and orders today" testId="v3-section-orders">
          <Row
            icon={FileText}
            title={`${alerts.quotesData?.quotes ?? 0} quotes · ${alerts.quotesData?.orders ?? 0} orders`}
            meta={`${money(alerts.quotesData?.quotes_value)} quoted · ${money(alerts.quotesData?.orders_value)} ordered`}
            onClick={alerts.openOrders}
            testId="v3-orders-row"
          />
        </Section>
    </div>
  );
};

// 8x8 calls (lime green, the 8x8 colour): open the 8x8 Calls window, jot a note for the call you are on, and see today's calls.
// A note is saved to the same Saved Notes the 8x8 window's Notes tab shows. The server matches it to the call once that call appears,
// and from then on it shows on that call in the 8x8 window and in Recent 8x8 calls here.
export const EightXEightTab = () => {
  const alerts = useV3Alerts();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const todaysNotes = useMemo(() => (alerts.quickNotes || [])
    .filter((note) => (note.text || '').trim() && isToday(note.created_at))
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), [alerts.quickNotes]);

  const calls = useMemo(() => [
    ...alerts.calls8x8MadeToday.map((call) => ({ call, logged: false })),
    ...alerts.calls8x8Logged.map((call) => ({ call, logged: true })),
  ].sort((a, b) => new Date(b.call.created_at || 0) - new Date(a.call.created_at || 0)), [alerts.calls8x8MadeToday, alerts.calls8x8Logged]);

  const noteFor = (call) => (alerts.quickNotes || []).find((note) => note.status === 'matched' && note.matched_call?.id === call.id);

  const save = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    const ok = await alerts.saveQuickNote(text);
    setSaving(false);
    if (ok) {
      setText('');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
    } else {
      toast.error('The note could not be saved. Please try again.');
    }
  };

  return (
    <div className="v3-tasks" data-testid="v3-tab-8x8">
      <Section title="Note for the call you're on" testId="v3-section-8x8-note">
        <div className="v3-note-box">
          <textarea
            className="v3-note-field"
            rows={5}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Type notes about the call you're on…"
            data-testid="v3-8x8-note-text"
          />
          <div className="v3-note-actions">
            <span className="v3-note-hint" data-testid="v3-8x8-note-hint">{justSaved ? 'Saved. It joins the call when the call appears.' : 'Saved notes attach to the call by themselves.'}</span>
            <button type="button" className="v3-lime-btn v3-lime-btn--sm" disabled={!text.trim() || saving} onClick={save} data-testid="v3-8x8-note-save">
              {saving ? <Loader2 className="v3-i v3-i--sm v3-spin" /> : null}<span>Save</span>
            </button>
          </div>
        </div>
      </Section>

      <button type="button" className="v3-lime-btn v3-lime-btn--bar" onClick={() => alerts.open8x8('made')} data-testid="v3-8x8-open">
        <PhoneCall className="v3-i v3-i--sm" /><span>Open 8x8 calls</span>
      </button>

      <Section title="Saved notes" count={todaysNotes.length} testId="v3-section-8x8-notes">
        {todaysNotes.length === 0 && <p className="v3-empty">No notes saved today</p>}
        {todaysNotes.slice(0, 5).map((note) => {
          const matched = note.status === 'matched' && note.matched_call;
          return (
            <button key={note.id} type="button" className="v3-note" onClick={() => alerts.open8x8('notes')} data-testid={`v3-8x8-note-${note.id}`}>
              <span className="v3-note-text">{note.text}</span>
              <span className={`v3-chip ${matched ? 'v3-chip--blue' : 'v3-chip--amber'}`}>
                {matched ? `Matched to ${note.matched_call.account_name || note.matched_call.phone_number || 'call'}` : 'Waiting to match a call…'}
              </span>
            </button>
          );
        })}
      </Section>

      <Section title="Recent 8x8 calls" count={calls.length} testId="v3-section-8x8-calls">
        {calls.length === 0 && <p className="v3-empty">No 8x8 calls today</p>}
        {calls.slice(0, 8).map(({ call, logged }) => {
          const note = noteFor(call);
          const Direction = call.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing;
          const who = call.account_name || call.eight_x8_name || call.phone_number || 'Unknown number';
          const meta = [fmtTime(call.created_at), call.phone_number].filter(Boolean).join(' · ');
          return (
            <div key={call.id} className="v3-item" data-testid={`v3-8x8-call-${call.id}`}>
              <button type="button" className="v3-item-button" onClick={() => alerts.open8x8(logged ? 'logged' : 'made')} title={note ? `${who} · ${note.text}` : who}>
                <span className="v3-item-ico v3-item-ico--lime"><Direction className="v3-i v3-i--sm" /></span>
                <span className="v3-item-text">
                  <span className="v3-item-title">{who}</span>
                  {meta && <span className="v3-item-meta">{meta}</span>}
                  {note && <span className="v3-item-note">“{note.text}”</span>}
                </span>
                <span className={`v3-badge ${logged ? 'v3-badge--done' : 'v3-badge--lime'}`}>{logged ? 'Logged' : 'To log'}</span>
              </button>
            </div>
          );
        })}
      </Section>
    </div>
  );
};
